import { useEffect, useRef, useState } from 'react'

// "Which holidays need checking?" — one shared answer for the toast, the sidebar badge
// and the settings page, so they never disagree and the dashboard asks the server once.
//
// The holiday job runs at night when no dashboard is open, so this is fetched on load
// (an SSE event alone would reach nobody) and again whenever something changes it: the
// job's 'holidays-review' event, or an import/review/save on the settings page.
// Plans/Standby_Erweiterungen_Plan.md §4a.

let entries = []          // [{ tenantId, tenantName, pending, years, failing, failingSince, missingYear }]
let loaded = false
let inFlight = null
const listeners = new Set()

export function refreshHolidayReview(apiFetch) {
  if (inFlight) return inFlight
  inFlight = apiFetch('/api/admin/holidays/pending-review')
    .then(r => (r.ok ? r.json() : null))
    .then(list => {
      if (!Array.isArray(list)) return
      entries = list
      loaded = true
      listeners.forEach(fn => fn(entries))
    })
    .catch(() => {})
    .finally(() => { inFlight = null })
  return inFlight
}

/** Number shown on the sidebar badge: entries to review, plus one per warning. */
export function holidayReviewCount(list) {
  return list.reduce((n, e) => n + e.pending + (e.failing ? 1 : 0) + (e.missingYear ? 1 : 0), 0)
}

/** Subscribes to the shared list; fetches it once per page load when `enabled`. */
export function useHolidayReview(apiFetch, enabled) {
  const [list, setList] = useState(entries)
  // apiFetch is recreated on every render, so it is read through a ref rather than
  // being an effect dependency that would refetch constantly.
  const apiFetchRef = useRef(apiFetch)
  apiFetchRef.current = apiFetch

  useEffect(() => {
    listeners.add(setList)
    return () => { listeners.delete(setList) }
  }, [])
  useEffect(() => {
    if (enabled && !loaded) refreshHolidayReview(apiFetchRef.current)
  }, [enabled])
  return enabled ? list : []
}

// ── "Morgen erinnern" ──────────────────────────────────────────────────────────
// Per browser, per tenant, until the next calendar day. A convenience only — the
// entries stay on the server until they are decided, and the toast comes back.

const snoozeKey = (tenantId) => `holiday-review-snooze:${tenantId}`
const localToday = () => new Date().toLocaleDateString('sv-SE') // YYYY-MM-DD

export function isSnoozed(tenantId) {
  try { return localStorage.getItem(snoozeKey(tenantId)) === localToday() } catch { return false }
}

export function snooze(tenantId) {
  try { localStorage.setItem(snoozeKey(tenantId), localToday()) } catch { /* private mode etc. */ }
}
